import { hashFnv1a } from '../rng';
import type { HorseStats, StatId } from '../race/types';
import { SEASON_DAYS, seasonDayOf, seasonOf } from './horse';

export const START_MONEY_CENTS = 50_000;
export const ENTRY_HORIZON_DAYS = SEASON_DAYS;

export type FeedPlanId = 'pasture' | 'hay' | 'oats' | 'premium';

export interface FeedPlan {
  id: FeedPlanId;
  name: string;
  costCents: number;
  energy: number;
}

/** Feed is the training fuel and the everyday money sink. Pasture is the
 * soft floor: free forever, so bankruptcy cannot dead-end the game. */
export const FEED_PLANS: Record<FeedPlanId, FeedPlan> = {
  pasture: { id: 'pasture', name: 'Pasture', costCents: 0, energy: 4 },
  hay: { id: 'hay', name: 'Hay', costCents: 2_000, energy: 8 },
  oats: { id: 'oats', name: 'Oats', costCents: 5_000, energy: 12 },
  premium: { id: 'premium', name: 'Premium Mash', costCents: 12_000, energy: 16 },
};

export const FEED_PLAN_IDS: readonly FeedPlanId[] = ['pasture', 'hay', 'oats', 'premium'];

export type FacilityId = 'gallopTrack' | 'enduranceTrail' | 'startingGate' | 'roundPen';

export interface FacilityDef {
  id: FacilityId;
  name: string;
  stat: StatId;
  /** Stat cap per facility level 1..4. */
  caps: [number, number, number, number];
  /** Cost to reach level 2 / 3 / 4. */
  upgradeCostsCents: [number, number, number];
}

const CAPS: [number, number, number, number] = [5, 10, 15, 20];
const UPGRADES: [number, number, number] = [30_000, 120_000, 400_000];

export const FACILITY_DEFS: Record<FacilityId, FacilityDef> = {
  gallopTrack: { id: 'gallopTrack', name: 'Gallop Track', stat: 'speed', caps: CAPS, upgradeCostsCents: UPGRADES },
  enduranceTrail: { id: 'enduranceTrail', name: 'Endurance Trail', stat: 'stamina', caps: CAPS, upgradeCostsCents: UPGRADES },
  startingGate: { id: 'startingGate', name: 'Starting Gate', stat: 'accel', caps: CAPS, upgradeCostsCents: UPGRADES },
  roundPen: { id: 'roundPen', name: 'Round Pen', stat: 'heart', caps: CAPS, upgradeCostsCents: UPGRADES },
};

export const FACILITY_IDS: readonly FacilityId[] = [
  'gallopTrack',
  'enduranceTrail',
  'startingGate',
  'roundPen',
];

export function facilityForStat(stat: StatId): FacilityDef {
  const def = FACILITY_IDS.map((id) => FACILITY_DEFS[id]).find((d) => d.stat === stat);
  if (!def) throw new Error(`No facility trains ${stat}`);
  return def;
}


export interface LeagueEconomy {
  entryFeeCents: number;
  purseCents: [number, number, number];
  cadenceDays: number;
  /** Wins required in the league below. */
  unlockWins: number;
}

export const DERBY_LEAGUE = 3;

export const LEAGUE_ECONOMY: readonly LeagueEconomy[] = [
  { entryFeeCents: 0, purseCents: [15_000, 6_000, 2_500], cadenceDays: 2, unlockWins: 0 },
  { entryFeeCents: 10_000, purseCents: [60_000, 24_000, 10_000], cadenceDays: 4, unlockWins: 3 },
  { entryFeeCents: 40_000, purseCents: [250_000, 100_000, 40_000], cadenceDays: 6, unlockWins: 3 },
  // The Grand Derby: last day of every season; two national wins to enter.
  { entryFeeCents: 100_000, purseCents: [1_000_000, 400_000, 150_000], cadenceDays: 12, unlockWins: 2 },
];

/** Which leagues run a race on this calendar day. */
export function racesOnDay(day: number): number[] {
  if (day < 1) return [];
  const seasonDay = seasonDayOf(day);
  const leagues: number[] = [];
  LEAGUE_ECONOMY.forEach((league, index) => {
    if (seasonDay % league.cadenceDays === 0) leagues.push(index);
  });
  return leagues;
}

export interface StableDef {
  stalls: [number, number, number, number];
  upgradeCostsCents: [number, number, number];
}

/** Barn capacity — how many horses can live behind the featured racer. */
export const STABLE_DEF: StableDef = {
  stalls: [2, 4, 6, 8],
  upgradeCostsCents: [40_000, 160_000, 500_000],
};

export function stableStalls(level: number): number {
  return STABLE_DEF.stalls[Math.max(0, Math.min(3, level - 1))]!;
}

/** Race events are identified deterministically: day·10 + league. */
export function eventId(day: number, league: number): number {
  return day * 10 + league;
}

export function eventDay(id: number): number {
  return Math.floor(id / 10);
}

export function eventLeague(id: number): number {
  return id % 10;
}

export function isValidEvent(id: number): boolean {
  const league = eventLeague(id);
  const day = eventDay(id);
  return league >= 0 && league < LEAGUE_ECONOMY.length && racesOnDay(day).includes(league);
}

export type RaceClassId = 'sprint' | 'middle' | 'route' | 'classic';

export interface RaceClassDef {
  id: RaceClassId;
  name: string;
  distanceM: number;
  /** Tank-drain multiplier: sprints are mash races, routes are
   * coast-management races. */
  drainMul: number;
  favoredStats: readonly StatId[];
  profile: string;
}

export const RACE_CLASSES: Record<RaceClassId, RaceClassDef> = {
  sprint: { id: 'sprint', name: 'Sprint', distanceM: 300, drainMul: 1.25, favoredStats: ['speed', 'accel'], profile: 'SPD + ACC' },
  middle: { id: 'middle', name: 'Middle', distanceM: 450, drainMul: 1, favoredStats: ['speed', 'stamina', 'accel', 'heart'], profile: 'Balanced' },
  route: { id: 'route', name: 'Route', distanceM: 650, drainMul: 0.8, favoredStats: ['stamina', 'heart'], profile: 'STA + HRT' },
  classic: { id: 'classic', name: 'Classic', distanceM: 550, drainMul: 0.95, favoredStats: ['speed', 'stamina', 'accel', 'heart'], profile: 'All-round' },
};

/** Every season contains a known spread; the order rotates by season. */
const CLASS_CALENDAR: readonly (readonly RaceClassId[])[] = [
  ['sprint', 'middle', 'route', 'sprint', 'middle', 'sprint'],
  ['sprint', 'middle', 'route'],
  ['middle', 'route'],
  ['classic'],
];

/** The card is knowable in advance: class is pure in (day, league). */
export function raceClassOf(day: number, league: number): RaceClassDef {
  const safeLeague = Math.max(0, Math.min(3, league));
  const sequence = CLASS_CALENDAR[safeLeague]!;
  if (safeLeague === DERBY_LEAGUE) return RACE_CLASSES.classic;
  const cadence = LEAGUE_ECONOMY[safeLeague]!.cadenceDays;
  const occurrence = Math.max(0, Math.floor(seasonDayOf(day) / cadence) - 1);
  const rotation = (seasonOf(day) - 1) % sequence.length;
  const id = sequence[(occurrence + rotation) % sequence.length]!;
  return RACE_CLASSES[id];
}

/**
 * Distance rewrites what the stats mean: over a route, sustained speed
 * IS stamina, and the gate barely matters; a sprint is all burst. This
 * blend — not the drain multiplier — is what lets a stayer beat a
 * sprinter at 650m and lose to it at 300m.
 */
export function classAdjustedStats(stats: HorseStats, classId: RaceClassId): HorseStats {
  const clamp = (value: number): number => Math.max(1, Math.min(20, Math.round(value)));
  switch (classId) {
    case 'sprint':
      return {
        speed: clamp(0.85 * stats.speed + 0.15 * stats.accel),
        stamina: clamp(stats.stamina * 0.8),
        accel: clamp(stats.accel * 1.15),
        heart: clamp(stats.heart * 0.8),
      };
    case 'route':
      return {
        speed: clamp(0.05 * stats.speed + 0.95 * stats.stamina),
        stamina: clamp(stats.stamina * 1.05),
        accel: clamp(stats.accel * 0.6),
        heart: clamp(stats.heart * 1.15),
      };
    case 'classic':
      return {
        speed: clamp(0.7 * stats.speed + 0.3 * stats.stamina),
        stamina: clamp(stats.stamina * 1.05),
        accel: clamp(stats.accel * 0.85),
        heart: clamp(stats.heart * 1.1),
      };
    default:
      return { ...stats };
  }
}

export type GoingId = 'fast' | 'good' | 'soft';
export type SurfaceId = 'dirt' | 'turf';

export interface Going {
  going: GoingId;
  surface: SurfaceId;
}

/** Weather is rolled per world and day, shown before entry — informed
 * gambles only, never invisible dice. */
export function goingOf(worldSeed: number, day: number, league: number): Going {
  const surface: SurfaceId = league >= 2 ? 'turf' : 'dirt';
  const roll = hashFnv1a(`${worldSeed}:going:${day}`) % 100;
  const going: GoingId = roll < 55 ? 'fast' : roll < 80 ? 'good' : 'soft';
  return { going, surface };
}

export function purseFor(league: number, place: number): number {
  const economy = LEAGUE_ECONOMY[league];
  if (!economy) return 0;
  return economy.purseCents[place - 1] ?? 0;
}
